// client/src/pages/admin/AdminUsers.js
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
import { adminAPI } from '../../services/api';
import { format } from 'date-fns';

const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  useEffect(() => {
    applyFilters();
  }, [users, roleFilter, searchTerm]);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      // Handle both formats: { users: [...] } or just [...]
      const data = response.data.users || response.data;
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.email?.toLowerCase().includes(term) ||
        u.profile?.businessName?.toLowerCase().includes(term) ||
        u.profile?.companyName?.toLowerCase().includes(term) ||
        u._id?.includes(term)
      );
    }

    setFilteredUsers(filtered);
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await adminAPI.updateBusiness(userId, { isActive: !currentStatus }); 
      // Note: The specific API call might differ slightly based on your exact route structure
      // If using the generic toggle route:
      // await axios.put(`/api/admin/users/${userId}/toggle-active`, {}, { headers: ... })
      
      // Optimistic update
      setUsers(users.map(u => u._id === userId ? { ...u, isActive: !u.isActive } : u));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (userId, email) => {
    setConfirmModal({
      title: 'Delete User',
      message: `Are you sure you want to PERMANENTLY DELETE user "${email}"?\n\nThis will remove:\n- User Login\n- Business/Wholesaler Profile\n- Order History\n- Products\n\nThis action cannot be undone.`,
      icon: 'danger',
      confirmLabel: 'Delete',
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setLoading(true);
          await adminAPI.deleteUser(userId);
          setUsers(users.filter(u => u._id !== userId));
          toast.success('User successfully deleted.');
        } catch (err) {
          toast.error('Error deleting user: ' + (err.response?.data?.message || err.message));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>;

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="bg-white shadow">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div>
             <button onClick={() => navigate('/admin/dashboard')} className="text-blue-600 hover:underline text-sm mb-1">← Dashboard</button>
             <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          </div>
          <button onClick={fetchUsers} className="text-gray-600 hover:text-blue-600">↻ Refresh</button>
        </div>
      </div>

      <div className="container mx-auto px-6 mt-8">
        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6 flex flex-col md:flex-row gap-4">
          <div className="md:w-1/4">
            <label className="text-xs font-bold text-gray-500 uppercase">Filter Role</label>
            <select 
              value={roleFilter} 
              onChange={e => setRoleFilter(e.target.value)}
              className="w-full mt-1 border rounded p-2"
            >
              <option value="all">All Roles</option>
              <option value="business">Business</option>
              <option value="wholesaler">Wholesaler</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="md:w-3/4">
             <label className="text-xs font-bold text-gray-500 uppercase">Search</label>
             <input 
                type="text" 
                placeholder="Search by Email, Company Name, or ID..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full mt-1 border rounded p-2"
             />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profile Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map(u => (
                <tr key={u._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                        u.role === 'admin' ? 'bg-red-500' : u.role === 'wholesaler' ? 'bg-purple-500' : 'bg-blue-500'
                      }`}>
                        {u.email[0].toUpperCase()}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{u.email}</div>
                        <div className="text-xs text-gray-400">{u._id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 capitalize text-sm text-gray-600">{u.role}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-800">
                    {u.profile?.businessName || u.profile?.companyName || <span className="text-gray-400 italic">No Profile</span>}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {u.isActive ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    {u.role !== 'admin' && (
                      <button 
                        onClick={() => handleDelete(u._id, u.email)}
                        className="bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1 rounded text-xs font-bold border border-red-200 transition-colors"
                      >
                        DELETE
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                    No users found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminUsers;